import AdminLayout from '@/layouts/AdminLayout.vue'

export default [
  {
    path: '/admin/login',
    name: 'LoginPage',
    component: () => import('@/views/admin/auth/LoginPage.vue'),
    meta: { title: 'Login' },
  },
  {
    path: '/admin/login/2fa',
    name: 'TwoFactorLoginPage',
    component: () => import('@/views/admin/auth/TwoFactorLoginPage.vue'),
    meta: { title: 'Two-Factor Authentication' },
  },
  {
    path: '/admin',
    component: AdminLayout,
    children: [
      {
        path: '',
        redirect: { name: 'DashboardPage' },
      },
      {
        path: 'welcome',
        name: 'WelcomePage',
        component: () => import('@/views/admin/WelcomePage.vue'),
        meta: { title: 'Welcome' },
      },
      {
        path: 'dashboard',
        name: 'DashboardPage',
        component: () => import('@/views/admin/DashboardPage.vue'),
        meta: { title: 'Dashboard', requiresAuth: true, permission: 'view dashboard' },
      },

      // Users
      {
        path: 'users',
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'UsersPage',
            component: () => import('@/views/admin/user/UsersPage.vue'),
            meta: { title: 'Users' },
          },
          {
            path: 'create',
            name: 'AddUserPage',
            component: () => import('@/views/admin/user/AddEditUserPage.vue'),
            meta: { title: 'Add User' },
          },
          {
            path: 'roles',
            name: 'UserRolesPage',
            component: () => import('@/views/admin/user/UserRolesPage.vue'),
            meta: { title: 'Roles' },
          },
          {
            path: 'security',
            name: 'UserSecurityPage',
            component: () => import('@/views/admin/user/UserSecurityPage.vue'),
            meta: { title: 'Security' },
          },
          {
            path: ':id',
            name: 'UserPage',
            component: () => import('@/views/admin/user/UserPage.vue'),
            meta: { title: 'User Profile' },
          },
          {
            path: ':id/edit',
            name: 'EditUserPage',
            component: () => import('@/views/admin/user/AddEditUserPage.vue'),
            meta: { title: 'Edit User' },
          },
        ],
      },

      // Suppliers
      {
        path: 'suppliers',
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'SuppliersPage',
            component: () => import('@/views/admin/supplier/SuppliersPage.vue'),
            meta: { title: 'Suppliers' },
          },
          {
            path: 'create',
            name: 'AddSupplierPage',
            component: () => import('@/views/admin/supplier/AddEditSupplierPage.vue'),
            meta: { title: 'Add Supplier' },
          },
          {
            path: 'parts',
            name: 'StocklistPage',
            component: () => import('@/views/admin/supplier/StocklistPage.vue'),
            meta: { title: 'Stock List', permission: 'view stock list' },
          },
          {
            path: 'stocklists/upload',
            name: 'UploadStockListPage',
            component: () => import('@/views/admin/supplier/UploadStocklistPage.vue'),
            meta: { title: 'Upload Stocklist', permission: 'upload stock list' },
            children: [
              {
                path: ':supplierId/stocklists/upload',
                name: 'SupplierUploadStockListPage',
                component: () => import('@/views/admin/supplier/UploadStocklistPage.vue'),
                meta: { title: 'Upload Stocklist', permission: 'upload stock list' },
              },
            ],
          },
          {
            path: ':supplierId/stocklists/:stockFileId/import',
            name: 'StocklistMappingPage',
            component: () => import('@/views/admin/supplier/StocklistMappingPage.vue'),
            meta: { title: 'Import Stocklist', permission: 'upload stock list' },
          },
          {
            path: ':id',
            name: 'SupplierPage',
            component: () => import('@/views/admin/supplier/SupplierPage.vue'),
            meta: { title: 'Supplier View' },
          },
          {
            path: ':id/edit',
            name: 'EditSupplierPage',
            component: () => import('@/views/admin/supplier/AddEditSupplierPage.vue'),
            meta: { title: 'Edit Supplier' },
          },
        ],
      },

      // Customers
      {
        path: 'customers',
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'CustomersPage',
            component: () => import('@/views/admin/customer/CustomersPage.vue'),
            meta: { title: 'Customers' },
            children: [
              {
                path: ':id/allies',
                name: 'CustomerAllies',
                component: () => import('@/views/admin/customer/CustomersPage.vue'),
                meta: { title: 'Customer Allies' },
              },
            ],
          },
          {
            path: 'create',
            name: 'AddCustomerPage',
            component: () => import('@/views/admin/customer/AddEditCustomerPage.vue'),
            meta: { title: 'Add Customer' },
          },
          {
            path: ':id',
            name: 'CustomerPage',
            component: () => import('@/views/admin/customer/CustomerPage.vue'),
            meta: { title: 'Customer View' },
          },
          {
            path: ':id/edit',
            name: 'EditCustomerPage',
            component: () => import('@/views/admin/customer/AddEditCustomerPage.vue'),
            meta: { title: 'Edit Customer' },
          },
        ],
      },

      // Purchase Analysis
      {
        path: 'purchase-analysis',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'order',
            name: 'PurchaseAnalysisOrderPage',
            component: () => import('@/views/admin/purchaseAnalysis/OrderPage.vue'),
            meta: { title: 'Purchase Analysis - Order', permission: 'view purchase analysis' },
          },
          {
            path: 'repair',
            name: 'PurchaseAnalysisRepairPage',
            component: () => import('@/views/admin/purchaseAnalysis/RepairPage.vue'),
            meta: { title: 'Purchase Analysis - Repair', permission: 'view purchase analysis' },
          },
        ],
      },

      // EQO
      {
        path: 'eqo',
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'EqoListPage',
            component: () => import('@/views/admin/eqo/EqoListPage.vue'),
            meta: { title: 'EQO', permission: 'view eqo' },
            children: [
              {
                path: 'customers/:customerId',
                name: 'CustomerEqoListPage',
                meta: { title: "Customer's EQO", permission: 'view eqo' },
              },
            ],
          },
          {
            path: 'create',
            name: 'AddEnquiryPage',
            component: () => import('@/views/admin/eqo/AddEnquiryPage.vue'),
            meta: { title: 'Add Enquiry', permission: 'add enquiry' },
          },
          {
            path: 'order-history',
            name: 'OrderHistoryPage',
            component: () => import('@/views/admin/eqo/OrderHistoryPage.vue'),
            meta: { title: 'Order History', permission: 'view order history' },
          },
          {
            path: 'part-sourcing',
            name: 'PartSourcingPage',
            component: () => import('@/views/admin/eqo/PartsSourcingPage.vue'),
            meta: { title: 'Part Sourcing' },
          },
          {
            path: ':id',
            name: 'EqoPage',
            component: () => import('@/views/admin/eqo/EqoPage.vue'),
            meta: { title: 'EQO View' },
          },
          {
            path: ':id/sourcing',
            name: 'PartsSourcing',
            component: () => import('@/views/admin/eqo/PartsSourcingPage.vue'),
            meta: { title: 'Parts Sourcing' },
          },
        ],
      },

      // Settings
      {
        path: 'settings',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'office-addresses',
            name: 'OfficeAddressesPage',
            component: () => import('@/views/admin/settings/OfficeAddressesPage.vue'),
            meta: { title: 'Office Addresses', permission: 'manage office addresses' },
          },
          {
            path: 'currencies',
            name: 'CurrenciesPage',
            component: () => import('@/views/admin/settings/CurrenciesPage.vue'),
            meta: { title: 'Currencies', permission: 'manage currencies' },
          },
          {
            path: 'regions',
            name: 'RegionsPage',
            component: () => import('@/views/admin/settings/RegionsPage.vue'),
            meta: { title: 'Regions', permission: 'manage regions' },
          },
          {
            path: 'departments',
            name: 'DepartmentsPage',
            component: () => import('@/views/admin/settings/DepartmentsPage.vue'),
            meta: { title: 'Departments', permission: 'manage departments' },
          },
          {
            path: 'industries',
            name: 'IndustriesPage',
            component: () => import('@/views/admin/settings/IndustriesPage.vue'),
            meta: { title: 'Industries', permission: 'manage industries' },
          },
          {
            path: 'payment-terms',
            name: 'PaymentTermsPage',
            component: () => import('@/views/admin/settings/PaymentTermsPage.vue'),
            meta: { title: 'Payment Terms', permission: 'manage payment terms' },
          },
          {
            path: 'designations',
            name: 'DesignationsPage',
            component: () => import('@/views/admin/settings/DesignationsPage.vue'),
            meta: { title: 'Designations', permission: 'manage designations' },
          },
          {
            path: 'lead-time',
            name: 'LeadTimePage',
            component: () => import('@/views/admin/settings/LeadTimePage.vue'),
            meta: { title: 'Lead Time', permission: 'manage lead time' },
          },
          {
            path: 'manufacturers',
            name: 'AdminManufacturersPage',
            component: () => import('@/views/admin/settings/ManufacturersPage.vue'),
            meta: { title: 'Manufacturers', permission: 'manage manufacturers' },
          },
          {
            path: 'supply-types',
            name: 'SupplyTypesPage',
            component: () => import('@/views/admin/settings/SupplyTypesPage.vue'),
            meta: { title: 'Supply Types', permission: 'manage supply types' },
          },
          {
            path: 'part-conditions',
            name: 'PartConditionsPage',
            component: () => import('@/views/admin/settings/PartConditionsPage.vue'),
            meta: { title: 'Part Conditions', permission: 'manage part conditions' },
          },
          {
            path: 'payment-methods',
            name: 'PaymentMethodsPage',
            component: () => import('@/views/admin/settings/PaymentMethodsPage.vue'),
            meta: { title: 'Payment Methods', permission: 'manage payment methods' },
          },
          {
            path: 'warranty-terms',
            name: 'WarrantyTermsPage',
            component: () => import('@/views/admin/settings/WarrantyTermsPage.vue'),
            meta: { title: 'Warranty Terms', permission: 'manage warranty terms' },
          },
          {
            path: 'calendar',
            name: 'CalendarPage',
            component: () => import('@/views/admin/settings/CalendarPage.vue'),
            meta: { title: 'Calendar', permission: 'manage calendar' },
          },
          {
            path: 'delivery-types',
            name: 'DeliveryTypesPage',
            component: () => import('@/views/admin/settings/DeliveryTypesPage.vue'),
            meta: { title: 'Delivery Types', permission: 'manage delivery types' },
          },
          {
            path: 'pdfs',
            name: 'PdfsPage',
            component: () => import('@/views/admin/settings/PdfsPage.vue'),
            meta: { title: 'PDFs', permission: 'manage pdfs' },
          },
          {
            path: 'eqo-notes',
            name: 'EqoNotesPage',
            component: () => import('@/views/admin/settings/EqoNotesPage.vue'),
            meta: { title: 'EQO Notes', permission: 'manage eqo notes' },
          },
          {
            path: 'activity-logs',
            name: 'ActivityLogsPage',
            component: () => import('@/views/admin/settings/ActivityLogsPage.vue'),
            meta: { title: 'Activity Logs', permission: 'view activity logs' },
          },
          {
            path: 'device-logins',
            name: 'DeviceLoginsPage',
            component: () => import('@/views/admin/settings/DeviceLoginsPage.vue'),
            meta: { title: 'Device Logins', permission: 'manage device logins' },
          },
        ],
      },

      // Website
      {
        path: 'website',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'blog',
            name: 'AdminBlogPage',
            component: () => import('@/views/admin/website/BlogPage.vue'),
            meta: { title: 'Blog', permission: 'manage blog' },
          },
          {
            path: 'faq',
            name: 'AdminFaqPage',
            component: () => import('@/views/admin/website/FaqPage.vue'),
            meta: { title: 'FAQ', permission: 'manage faq' },
          },
          {
            path: 'team-contact-addresses',
            name: 'AdminTeamContactAddressesPage',
            component: () => import('@/views/admin/website/TeamContactAddressPage.vue'),
            meta: { title: 'Team contact addresses', permission: 'manage team contact addresses' },
          },
          {
            path: 'plant-addresses',
            name: 'AdminPlantAddressesPage',
            component: () => import('@/views/admin/website/PlantAddressPage.vue'),
            meta: { title: 'Plant address and bank details', permission: 'manage plant address and bank details' },
          },
          {
            path: 'legal-documents',
            name: 'AdminLegalDocumentsPage',
            component: () => import('@/views/admin/website/LegalDocumentPage.vue'),
            meta: { title: 'Legal Documents', permission: 'manage legal documents' },
          },
          {
            path: 'parts',
            children: [
              {
                path: '',
                name: 'WebsiteStocklistPage',
                component: () => import('@/views/admin/website/StocklistPage.vue'),
                meta: { title: 'Website Stock List', permission: 'view website stock list' },
              },
              {
                path: 'add',
                name: 'WebsitePartAddPage',
                component: () => import('@/views/admin/website/AddEditPartPage.vue'),
                meta: { title: 'Add Website Part', permission: 'upload website stock list' },
              },
              {
                path: ':partId/edit',
                name: 'WebsitePartEditPage',
                component: () => import('@/views/admin/website/AddEditPartPage.vue'),
                meta: { title: 'Edit Website Part', permission: 'upload website stock list' },
              },
            ],
          },
          {
            path: 'stocklists',
            children: [
              {
                path: 'upload',
                name: 'WebsiteUploadStockListPage',
                component: () => import('@/views/admin/website/UploadStocklistPage.vue'),
                meta: { title: 'Upload Stocklist', permission: 'upload website stock list' },
              },
              {
                path: ':stockFileId/import',
                name: 'WebsiteStocklistMappingPage',
                component: () => import('@/views/admin/website/StocklistMappingPage.vue'),
                meta: { title: 'Import Stocklist', permission: 'upload website stock list' },
              },
            ],
          },
        ],
      },
    ],
  },
]
